import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox, PrimaryButton } from 'component-library';
import _ from 'lodash';
import { useState } from 'react';
import listImg from '../../../../assets/lcpIcons/autolisttest-01.png';
import autoTrimImg from '../../../../assets/lcpIcons/autotrimtest-01.png';
import coTurnImg from '../../../../assets/lcpIcons/coordinatedturn-new-01.png';
import functionSetupImg from '../../../../assets/lcpIcons/functionsetup-new-01.png';
import pitchImg from '../../../../assets/lcpIcons/waves-01.png';
import listImgWhite from '../../../../assets/lcpIcons/white/autolisttest-01.png';
import autoTrimImgWhite from '../../../../assets/lcpIcons/white/autotrimtest-01.png';
import coTurnImgWhite from '../../../../assets/lcpIcons/white/coordinatedturn-new-01.png';
import functionSetupImgWhite from '../../../../assets/lcpIcons/white/functionsetup-new-01.png';
import pitchImgWhite from '../../../../assets/lcpIcons/white/waves-01.png';
import { useTheme } from '../../../../contexts/ThemeContext';
import { KeyCountWrapper, Preset } from '../../../../models/ApiModels';
import Modal from '../../../../utils/Modal';
import ColumnChart from '../../../Dashboard/ColumnChart';

interface Props {
  autoTrimData: {
    id: string;
    name: string;
    value: number | boolean | number[];
    unit: string | number[];
    defaultValue: number | boolean | number[];
    displayValue?: string;
  }[];
  listRollData: { id: string; name: string; value: number; unit: string; defaultValue: number }[];
  coTurnData: {
    id: string;
    name: string;
    value: number | number[];
    unit: string;
    defaultValue: number | number[];
    displayValue?: string;
  }[];
  pitchData: { id: string; name: string; value: number; unit: string; defaultValue: number }[];
  selectBoxes?: boolean;
  preset?: Preset;
  setPreset?: (preset: Preset) => void;
  isPreset?: boolean;
  compareDefault: boolean;
}

export function ConfigurationFunctionSetupSettings(props: Props) {
  const { resolvedTheme } = useTheme();

  function handleEnabledValues(clickedName: any, clickedValue: any, clickedId: string) {
    if (
      clickedName.includes('Theme') ||
      clickedName.includes('Orientation') ||
      clickedName.includes('Allow nofix') ||
      clickedName.includes('Prioritize waterspeed')
    ) {
      if (props.preset && props.setPreset) {
        const newPreset = { ...props.preset };
        if (Object.prototype.hasOwnProperty.call(newPreset.lcpMetadata, clickedId)) {
          //check if key exists
          delete newPreset.lcpMetadata[clickedId];
        } else {
          newPreset.lcpMetadata[clickedId] = clickedValue;
        }
        props.setPreset(newPreset);
      }
    } else {
      if (props.preset && props.setPreset) {
        const newPreset = { ...props.preset };
        if (Object.prototype.hasOwnProperty.call(newPreset.lcuMetadata, clickedId)) {
          //check if key exists
          delete newPreset.lcuMetadata[clickedId];
        } else {
          // If the value is an array (e.g., curve data), convert it to a string before setting it in the preset
          const arrayValueToString = Array.isArray(clickedValue) ? clickedValue.join(',') : clickedValue;
          newPreset.lcuMetadata[clickedId] = arrayValueToString;
        }
        props.setPreset(newPreset);
      }
    }
  }

  return (
    <div>
      <h4 className='prose-heading4 pb-5 mb-5 border-b border-gray-10'>
        <img
          src={resolvedTheme === 'light' ? functionSetupImg : functionSetupImgWhite}
          className='inline mr-1 object-contain'
          alt='function setup'
        />
        Function setup settings
      </h4>
      <div className='grid grid-cols-[repeat(auto-fit,minmax(16rem,auto))] gap-10 border-b border-gray-10 pb-5'>
        {/* Auto trim */}
        <div className='flex flex-col gap-4'>
          <h5 className='prose-heading5'>
            <img
              src={resolvedTheme === 'light' ? autoTrimImg : autoTrimImgWhite}
              className='inline mr-1 object-contain'
              alt='auto trim'
            />
            Auto trim
          </h5>
          {props.autoTrimData
            .filter(({ id, value }) => !(id === 'man_TrimMemoryEnabled' && !value)) //hide trim memory settings if trim memory is not enabled
            .map(({ name, value, id, unit, defaultValue, displayValue }) => (
              <div
                key={id}
                className={`${
                  props.compareDefault && !_.isEqual(value, defaultValue) && 'text-primary-100 font-bold'
                } ${props.compareDefault && _.isEqual(value, defaultValue) && 'text-gray-60'} flex gap-2`}
              >
                {props.selectBoxes && (
                  <Checkbox
                    disabled={value === null || value === undefined}
                    active={
                      Object.keys(props.preset?.lcpMetadata as object).includes(id) ||
                      Object.keys(props.preset?.lcuMetadata as object).includes(id)
                    }
                    onClick={() => handleEnabledValues(name, value, id)}
                  />
                )}
                <p
                  className={`${
                    (value === null && props.isPreset) ||
                    (value === undefined && props.isPreset) ||
                    (Array.isArray(value) && value.length === 0 && props.isPreset)
                      ? 'text-gray-40'
                      : ''
                  }`}
                >
                  {name}
                </p>
                {value === null || value === undefined || (Array.isArray(value) && value.length === 0) ? (
                  <p className='text-gray-40 italic ml-auto'>{props.isPreset ? '-' : 'n/a'}</p>
                ) : (
                  <>
                    {['at_Curve1', 'at_Curve2', 'at_Curve3', 'at_Curve4'].includes(id) && Array.isArray(value) ? (
                      <HoverBox id={id} curveArray={value} text={unit} />
                    ) : (
                      <p className='font-code ml-auto'>
                        {id === 'man_TrimMemoryEnabled' ? (
                          <FontAwesomeIcon icon={faCheck} />
                        ) : (
                          displayValue || value + unit.toString()
                        )}
                      </p>
                    )}
                  </>
                )}
              </div>
            ))}
        </div>
        {/* List & Roll */}
        <div className='flex flex-col gap-4'>
          <h5 className='prose-heading5'>
            <img
              src={resolvedTheme === 'light' ? listImg : listImgWhite}
              className='inline mr-1 object-contain'
              alt='list and roll'
            />
            List & Roll
          </h5>
          {props.listRollData.map(({ name, value, id, unit, defaultValue }) => (
            <div
              key={id}
              className={`${props.compareDefault && !_.isEqual(value, defaultValue) && 'text-primary-100 font-bold'} ${
                props.compareDefault && _.isEqual(value, defaultValue) && 'text-gray-60'
              } flex gap-2`}
            >
              {props.selectBoxes && (
                <Checkbox
                  disabled={value === null || value === undefined}
                  active={
                    Object.keys(props.preset?.lcpMetadata as object).includes(id) ||
                    Object.keys(props.preset?.lcuMetadata as object).includes(id)
                  }
                  onClick={() => handleEnabledValues(name, value, id)}
                />
              )}
              <p
                className={`${
                  (value === null && props.isPreset) || (value === undefined && props.isPreset) ? 'text-gray-40' : ''
                }`}
              >
                {name}
              </p>
              {value === null || value === undefined ? (
                <p className='text-gray-40 italic ml-auto'>{props.isPreset ? '-' : 'n/a'}</p>
              ) : (
                <p className='font-code ml-auto'>{value + unit}</p>
              )}
            </div>
          ))}
        </div>
        {/* Coordinated turn */}
        <div className='flex flex-col gap-4'>
          <h5 className='prose-heading5'>
            <img
              src={resolvedTheme === 'light' ? coTurnImg : coTurnImgWhite}
              className='inline mr-1 object-contain'
              alt='coordinated turn'
            />
            Coordinated turn
          </h5>
          {props.coTurnData.map(({ name, value, id, unit, defaultValue, displayValue }) => (
            <div
              key={id}
              className={`${props.compareDefault && !_.isEqual(value, defaultValue) && 'text-primary-100 font-bold'} ${
                props.compareDefault && _.isEqual(value, defaultValue) && 'text-gray-60'
              } flex gap-2`}
            >
              {props.selectBoxes && (
                <Checkbox
                  disabled={value === null || value === undefined}
                  active={
                    Object.keys(props.preset?.lcpMetadata as object).includes(id) ||
                    Object.keys(props.preset?.lcuMetadata as object).includes(id)
                  }
                  onClick={() => handleEnabledValues(name, value, id)}
                />
              )}
              <p
                className={`${
                  (value === null && props.isPreset) ||
                  (value === undefined && props.isPreset) ||
                  (Array.isArray(value) && value.length === 0 && props.isPreset)
                    ? 'text-gray-40'
                    : ''
                }`}
              >
                {name}
              </p>
              {value === null || value === undefined || (Array.isArray(value) && value.length === 0) ? (
                <p className='text-gray-40 italic ml-auto'>{props.isPreset ? '-' : 'n/a'}</p>
              ) : (
                <>
                  {Array.isArray(value) ? (
                    <HoverBox id={id} curveArray={value} text={unit} />
                  ) : (
                    <p className='font-code ml-auto'>{displayValue || value + unit}</p>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        {/* Pitch control */}
        <div className='flex flex-col gap-4'>
          <h5 className='prose-heading5'>
            <img
              src={resolvedTheme === 'light' ? pitchImg : pitchImgWhite}
              className='inline mr-1 object-contain'
              alt='pitch control'
            />
            Pitch control
          </h5>
          {props.pitchData.map(({ name, value, id, unit, defaultValue }) => (
            <div
              key={id}
              className={`${props.compareDefault && !_.isEqual(value, defaultValue) && 'text-primary-100 font-bold'} ${
                props.compareDefault && _.isEqual(value, defaultValue) && 'text-gray-60'
              } flex gap-2`}
            >
              {props.selectBoxes && (
                <Checkbox
                  disabled={value === null || value === undefined}
                  active={
                    Object.keys(props.preset?.lcpMetadata as object).includes(id) ||
                    Object.keys(props.preset?.lcuMetadata as object).includes(id)
                  }
                  onClick={() => handleEnabledValues(name, value, id)}
                />
              )}
              <p
                className={`${
                  (value === null && props.isPreset) || (value === undefined && props.isPreset) ? 'text-gray-40' : ''
                }`}
              >
                {name}
              </p>
              {value === null || value === undefined ? (
                <p className='text-gray-40 italic ml-auto'>{props.isPreset ? '-' : 'n/a'}</p>
              ) : (
                <p className='font-code ml-auto'>{value + unit}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

interface HoverBoxProps {
  id: string;
  curveArray: number[];
  text?: number[] | string;
}

function HoverBox(props: HoverBoxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useState<HTMLButtonElement | null>(null)[1];

  const curveSeries: KeyCountWrapper[] = props.curveArray.map((count, index) => ({
    key: index.toString(),
    count,
    diff: 0,
  }));

  return (
    <>
      <button
        className='font-code font-bold flex text-white bg-primary-80 px-2 ml-auto'
        type='button'
        onClick={() => setIsOpen(!isOpen)}
        aria-label='Show curve'
        ref={buttonRef as any}
      >
        {'Show ' + (props.text + ' curve')}
      </button>
      {isOpen && (
        <Modal>
          <div className='text-black'>
            <ColumnChart colorSetting='curveArray' hideLabels series={curveSeries} compare={false} />
            <PrimaryButton label='Click to close' onClick={() => setIsOpen(false)} />
          </div>
        </Modal>
      )}
    </>
  );
}
